import React, { useState, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import { AssetTypeStyleEditor } from './AssetStyleEditor';
import { ChevronDown, MapPin, Paintbrush, Search, X } from 'lucide-react';

import { useMapStore } from '@/stores/mapStore';
import { GeomType } from '@/types/enums';

import { cn } from '@/lib/utils';
import { DEFAULT_LAYER_STYLE, LayerStyleConfig } from '@/types/layers';
import { ASSET_CONFIG } from '@/types/asset-config';
import { FeatureLayerEditor } from './FeatureLayerEditor';
import { CorrectionOverlayPanel } from './CorrectionOverlayPanel';

// Get the type of a single layer from the Zustand store
//@ts-ignore
type LayerConfig = ReturnType<typeof useMapStore>['layers'][number];

interface MapSidebarProps {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
}

export const MapSidebar = ({ sidebarOpen, toggleSidebar }: MapSidebarProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);

  const allLayers = useMapStore((state) => state.layers);
  const toggleLayerVisibility = useMapStore(
    (state) => state.toggleLayerVisibility,
  );

  // const { updateAssetStyle } = useUpdateAssetStyles();
  const overlayCorrections = useMapStore((s) => s.overlayCorrections);
  const overlayVisible = useMapStore((s) => s.overlayVisible);
  const setOverlayVisible = useMapStore((s) => s.setOverlayVisible);

  const hasCorrections = overlayCorrections.length > 0;

  // Get operational layers
  const operationalLayers = useMemo(
    () => allLayers.filter((layer) => layer.sourceTable !== 'assets'),
    [allLayers],
  );

  // Get asset layers
  const assetLayers = useMemo(
    () => allLayers.filter((layer) => layer.sourceTable === 'assets'),
    [allLayers],
  );

  // Group asset layers by category and filter by search term
  const groupedAssetLayers = useMemo(() => {
    const filteredLayers = assetLayers.filter((layer) =>
      layer.displayName.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    const groups = new Map<string, LayerConfig[]>();
    for (const layer of filteredLayers) {
      const category = layer.category || 'Uncategorized';
      if (!groups.has(category)) {
        groups.set(category, []);
      }
      groups.get(category)!.push(layer);
    }

    return Array.from(groups.entries()).sort((a, b) =>
      a[0].localeCompare(b[0]),
    );
  }, [assetLayers, searchTerm]);

  const totalVisibleCount = useMemo(
    () => allLayers.filter((l) => l.isVisible).length,
    [allLayers],
  );
  const totalLayerCount = allLayers.length;

  const handleToggleCategory = (
    layersInGroup: LayerConfig[],
    enabled: boolean,
  ) => {
    layersInGroup.forEach((layer) => {
      if (layer.isVisible !== enabled) {
        toggleLayerVisibility(layer.id);
      }
    });
  };

  const isCategoryVisible = (layersInGroup: LayerConfig[]) => {
    if (layersInGroup.length === 0) return false;
    return layersInGroup.every((layer) => layer.isVisible);
  };

  return (
    <div
      className={cn(
        'absolute top-0 right-0 z-50 h-full transition-transform duration-300 ease-in-out flex flex-col',
        'bg-background/95 backdrop-blur-md border-l border-border shadow-2xl',
        'w-full md:w-85 max-h-[720px]:w-full',
        sidebarOpen ? 'translate-x-0' : 'translate-x-full',
      )}
    >
      {/* Header */}
      <div className="p-4 border-b border-border space-y-4 shrink-0 bg-muted/10">
        <div className="flex items-center gap-3">
          <MapPin className="h-5 w-5 text-secondary" />
          <h2 className="text-lg font-semibold text-foreground">Layers</h2>
          <Badge
            variant="outline"
            className="ml-auto font-mono text-xs dark:bg-secondary"
          >
            {totalVisibleCount}/{totalLayerCount}
          </Badge>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
            onClick={toggleSidebar}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter layers..."
            className="pl-9 bg-background/50"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="p-4 space-y-6 pb-20">
            {/* Temporary Layers — only shows when ticket with corrections is open */}
            <CorrectionOverlayPanel />
            {/* Operational Layers Section */}
            {operationalLayers.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Operational Layers
                  </h3>
                </div>
                <div className="space-y-2">
                  {operationalLayers.map((layer) => (
                    <Collapsible
                      key={layer.id}
                      defaultOpen
                      className="rounded-lg border border-border bg-card shadow-sm overflow-hidden"
                    >
                      <CollapsibleContent forceMount>
                        <div className="flex items-center justify-between py-2 px-4 hover:bg-muted/30 transition-colors group">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <StyleLegendIcon layer={layer} />

                            <Label
                              htmlFor={`switch-${layer.id}`}
                              className="cursor-pointer text-sm truncate select-none text-foreground font-normal  transition-colors"
                            >
                              {layer.displayName}
                            </Label>
                          </div>

                          <div className="flex items-center gap-1 ml-2 shrink-0">
                            {/* TODO: MAKE THIS MORE ROBUST BY SETTING PERMISSIONS PER LAYER */}
                            {layer.key !== 'CREW_LOCATION' && (
                              <Popover
                                open={openPopoverId === layer.id}
                                onOpenChange={(isOpen) =>
                                  setOpenPopoverId(isOpen ? layer.id! : null)
                                }
                              >
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className={cn(
                                      'h-7 w-7 opacity-0 group-hover:opacity-100 transition-all focus:opacity-100',
                                      'text-muted-foreground hover:bg-secondary/10 hover:text-secondary',
                                      openPopoverId === layer.id &&
                                        'opacity-100 text-secondary bg-secondary/10',
                                    )}
                                  >
                                    <Paintbrush className="h-3.5 w-3.5" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="w-80"
                                  side="left"
                                  align="start"
                                >
                                  <FeatureLayerEditor
                                    layer={layer}
                                    onClose={() => setOpenPopoverId(null)}
                                  />
                                </PopoverContent>
                              </Popover>
                            )}

                            <Switch
                              id={`switch-${layer.id}`}
                              checked={layer.isVisible}
                              onCheckedChange={() =>
                                toggleLayerVisibility(layer.id!)
                              }
                              className="scale-90 bg-secondary data-[state=checked]:bg-secondary hover:cursor-pointer"
                            />
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  ))}
                </div>
              </div>
            )}

            {/* Asset Layers Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Asset Layers
                </h3>
              </div>

              <div className="space-y-3">
                {groupedAssetLayers.map(([categoryName, layersInGroup]) => (
                  <Collapsible
                    key={categoryName}
                    defaultOpen
                    className="rounded-lg border border-border bg-card shadow-sm overflow-hidden"
                  >
                    <div className="flex items-center justify-between px-3 py-2 bg-muted/30 border-b border-border/50">
                      <CollapsibleTrigger className="group/trigger flex items-center gap-2 font-medium text-sm flex-1 text-left text-foreground hover:text-secondary transition-colors cursor-pointer select-none">
                        <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-data-[state=closed]/trigger:-rotate-90 group-hover/trigger:text-secondary" />
                        {categoryName}
                      </CollapsibleTrigger>
                      <Switch
                        checked={isCategoryVisible(layersInGroup)}
                        onCheckedChange={(checked) =>
                          handleToggleCategory(layersInGroup, checked)
                        }
                        className="scale-90 bg-secondary data-[state=checked]:bg-secondary hover:cursor-pointer"
                      />
                    </div>

                    <CollapsibleContent>
                      <div>
                        {layersInGroup.map((layer) => (
                          <div
                            key={layer.id}
                            className="flex items-center justify-between py-2 px-4 hover:bg-muted/30 transition-colors group border-b last:border-0 border-border/40"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <StyleLegendIcon layer={layer} />

                              <Label
                                htmlFor={`switch-${layer.id}`}
                                className="cursor-pointer text-sm truncate select-none text-foreground font-normal  transition-colors"
                              >
                                {layer.displayName}
                              </Label>
                            </div>

                            <div className="flex items-center gap-2 ml-2 shrink-0">
                              <Popover
                                open={openPopoverId === layer.id}
                                onOpenChange={(isOpen) =>
                                  setOpenPopoverId(isOpen ? layer.id : null)
                                }
                              >
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className={cn(
                                      'h-7 w-7 opacity-0 group-hover:opacity-100 transition-all focus:opacity-100',
                                      'text-muted-foreground hover:bg-secondary/10 hover:text-secondary',
                                      openPopoverId === layer.id &&
                                        'opacity-100 text-secondary bg-secondary/10',
                                    )}
                                  >
                                    <Paintbrush className="h-3.5 w-3.5" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="w-80"
                                  side="left"
                                  align="start"
                                >
                                  <FeatureLayerEditor
                                    layer={layer}
                                    onClose={() => setOpenPopoverId(null)}
                                  />
                                  {/* <AssetTypeStyleEditor
                                    initialSettings={{
                                      styles:
                                        (layer.styles as unknown as LayerStyleConfig) ||
                                        {},
                                      category: layer.category || undefined,
                                      zIndex: layer.zIndex,
                                      opacity: Number(layer.opacity ?? 1),
                                      minZoom: layer.minZoom,
                                      maxZoom: layer.maxZoom,
                                    }}
                                    geomType={layer?.geomType as GeomType}
                                    onSave={(updatedSettings) => {
                                      updateAssetStyle({
                                        id:
                                          layer.id.split('asset-')[1] ||
                                          layer.id,
                                        ...updatedSettings,
                                      });
                                      setOpenPopoverId(null);
                                    }}
                                    onClose={() => setOpenPopoverId(null)}
                                  /> */}
                                </PopoverContent>
                              </Popover>

                              <Switch
                                id={`switch-${layer.id}`}
                                checked={layer.isVisible}
                                onCheckedChange={() =>
                                  toggleLayerVisibility(layer.id)
                                }
                                className="scale-90 bg-secondary data-[state=checked]:bg-secondary hover:cursor-pointer"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>
            </div>
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

const StyleLegendIcon = ({ layer }: { layer: LayerConfig }) => {
  const styles = layer.styles as any;
  const geomType = layer?.geomType?.toLowerCase();

  const color = styles?.color || DEFAULT_LAYER_STYLE.color;
  const strokeColor = styles?.strokeColor || DEFAULT_LAYER_STYLE.strokeColor;

  if (geomType === 'point' || geomType === 'multipoint') {
    const pointType = styles?.pointType || 'circle';

    if (pointType === 'icon' && styles?.iconUrl) {
      return (
        <div className="h-5 w-5 shrink-0 flex items-center justify-center">
          <img
            src={styles.iconUrl}
            alt="icon"
            className="w-full h-full object-contain"
            style={{
              filter: styles.color
                ? `drop-shadow(0 0 1px ${styles.color})`
                : undefined,
              transform: styles.iconRotation
                ? `rotate(${styles.iconRotation}deg)`
                : undefined,
            }}
          />
        </div>
      );
    }

    return (
      <div className="h-5 w-5 shrink-0 flex items-center justify-center">
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          className="overflow-visible"
        >
          {pointType === 'square' && (
            <rect
              x="2"
              y="2"
              width="12"
              height="12"
              fill={color}
              stroke={strokeColor}
              strokeWidth="1.5"
            />
          )}
          {pointType === 'triangle' && (
            <polygon
              points="8,1 15,14 1,14"
              fill={color}
              stroke={strokeColor}
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
          )}
          {pointType === 'star' && (
            <polygon
              points="8,1 10,6 15,6 11,9 12.5,14 8,11 3.5,14 5,9 1,6 6,6"
              fill={color}
              stroke={strokeColor}
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
          )}
          {(pointType === 'circle' ||
            !['square', 'triangle', 'star'].includes(pointType)) && (
            <circle
              cx="8"
              cy="8"
              r="6"
              fill={color}
              stroke={strokeColor}
              strokeWidth="1.5"
            />
          )}
        </svg>
      </div>
    );
  }

  if (geomType === 'linestring' || geomType === 'multilinestring') {
    const isDashed = styles?.lineDash && styles?.lineDash.length > 0;
    const dashStyle =
      styles?.linePattern === 'dotted'
        ? 'dotted'
        : isDashed
          ? 'dashed'
          : 'solid';

    return (
      <div className="h-4 w-4 shrink-0 flex items-center justify-center">
        <div
          style={{
            width: '100%',
            height: 0,
            borderBottomWidth: `${Math.max(2, styles?.lineWidth || 2)}px`,
            borderBottomColor: styles?.color || color,
            borderBottomStyle: dashStyle,
            transform: 'rotate(-15deg)',
            opacity: 0.9,
          }}
        />
      </div>
    );
  }

  if (geomType === 'polygon' || geomType === 'multipolygon') {
    return (
      <div
        className="h-4 w-4 shrink-0 rounded-sm"
        style={{
          backgroundColor: styles?.fillColor || color,
          border: `2px solid ${styles?.strokeColor || styles?.color || '#000'}`,
          opacity: styles?.opacity ?? 0.7,
        }}
      />
    );
  }

  return (
    <div className="h-4 w-4 shrink-0 bg-muted rounded-sm border border-border" />
  );
};
